import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { DayPassProvider, useDayPass } from '@/contexts/DayPassContext';
import { OptimizedImage } from '@/components/OptimizedImage';
import ActivityCard from '@/components/activities/ActivityCard';
import ActivityModal from '@/components/activities/ActivityModal';
import WhyChooseSection from '@/components/activities/WhyChooseSection';
import DayPassPricingSection from '@/components/activities/DayPassPricingSection';
import LocationAccessSection from '@/components/activities/LocationAccessSection';
import AmenitiesOverviewSection from '@/components/activities/AmenitiesOverviewSection';
import DayGuestFAQ from '@/components/activities/DayGuestFAQ';
import DayGuestCTAContact from '@/components/activities/DayGuestCTAContact';
import DayGuestExperienceVideo from '@/components/activities/DayGuestExperienceVideo';
import { DayPassBookingTray } from '@/components/activities/DayPassBookingTray';
import NewHeader from '@/components/NewHeader';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Calendar, ChevronDown } from 'lucide-react';
import SEO from '@/components/SEO';
import { getPageSEO } from '@/data/seoTranslations';
import type { SEOLanguage } from '@/data/seoTranslations';
import { getBlurPlaceholder } from '@/utils/blurPlaceholders';
import { trackActivityModalOpen, trackScrollDepth } from '@/utils/analytics';

const ActivitiesContent: React.FC = () => {
    const { t, language } = useLanguage();
    const { setIsTrayOpen } = useDayPass();
    const [selectedActivity, setSelectedActivity] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    
    // SSR-safe animation control: only enable animations after component mounts on client
    const [isMounted, setIsMounted] = useState(false);
    
    // Mobile hero section state - Initialize with SSR-safe defaults
    const [isMobile, setIsMobile] = useState(false);
    const [currentSubtitleIndex, setCurrentSubtitleIndex] = useState(0);
    const subtitleIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false); // Toggle for showing all cards
    
    // Set mounted state on client-side hydration
    useEffect(() => {
        setIsMounted(true);
    }, []);
    

    // Get SEO data for the activities page
    const seoLang = language === 'zh-CN' || language === 'ko' ? language : (['en', 'de', 'fr'].includes(language) ? language : 'en') as SEOLanguage;
    const seoData = getPageSEO('activities', seoLang);

    // Activity Arcade items arranged for a 4-column Bento grid with 8 total items (7 small, 1 vertical hero)
    // All activities are included in Day Pass (except those marked "Coming Soon")
    // All activities now use modals for consistent user experience
    const activitiesList = [
        {
            id: 'dining',
            image: "/images/activity arcade/Bento Grid/Restarunt Jungfrau 3_2.webp",
            imageMobile: "/images/activity arcade/Bento Grid/Restarunt Jungfrau 3_2_mobile.webp",
            logo: "/images/jungfrau dark 100x100.webp",
            actionType: 'modal' as const,
            dayPassBadge: true
        },
        {
            id: 'sauna',
            image: "/images/activity arcade/Bento Grid/Sauna Wide.webp",
            imageMobile: "/images/activity arcade/Bento Grid/Sauna 3_2_mobile.webp",
            logo: "/images/NUTRASWISS-LOGO.png",
            actionType: 'modal' as const,
            dayPassBadge: true
        },
        {
            id: 'bistro_dine_in',
            image: "/images/activity arcade/Bento Grid/Carbon Mountain cafe wide.webp",
            imageMobile: "/images/activity arcade/Bento Grid/Carbon Mountain Cafe 3_2_mobile.webp",
            logo: "/images/Carbon mountain cafe logo.png",
            actionType: 'modal' as const,
            dayPassBadge: true // Included in Day Pass
        },
        {
            id: 'spa',
            image: "/images/activity arcade/Bento Grid/nutraswiss spa.webp",
            logo: "/images/NUTRASWISS-LOGO.png",
            actionType: 'modal' as const,
            dayPassBadge: true
        },
        {
            id: 'cinema',
            image: "/images/activity arcade/Bento Grid/Cinema 3_2.webp",
            imageMobile: "/images/activity arcade/Bento Grid/Cinema 3_2_mobile.webp",
            // badge: t("activities.comingSoon"),
            actionType: 'modal' as const,
            dayPassBadge: false // Coming Soon, not yet included
        },
        {
            id: 'billiards',
            image: "/images/activity arcade/Bento Grid/Billiards Room 3_2.webp",
            imageMobile: "/images/activity arcade/Bento Grid/Billiards Room 3_2_mobile.webp",
            actionType: 'modal' as const,
            dayPassBadge: true
        },
        {
            id: 'pool',
            image: "/images/activity arcade/Bento Grid/pool_compressed.optimized.webp",
            badge: t("activities.comingSoon"),
            actionType: 'modal' as const,
            dayPassBadge: false // Coming Soon, not yet included
        },
        {
            id: 'sports',
            image: "/images/activity arcade/Bento Grid/Tennis 3_2.webp",
            imageMobile: "/images/activity arcade/Bento Grid/Tennis 3_2_mobile.webp",
            badge: t("activities.comingSoon"),
            actionType: 'modal' as const,
            dayPassBadge: false // Coming Soon, not yet included
        }
    ];

    const handleOpenModal = (id: string) => {
        const activity = activitiesList.find(a => a.id === id);
        if (activity) {
            const activityName = t(`activities.items.${id}.title`);
            const categoryMap: Record<string, string> = {
                'dining': 'Dining',
                'bistro_dine_in': 'Dining',
                'sauna': 'Wellness',
                'spa': 'Wellness',
                'cinema': 'Entertainment',
                'billiards': 'Entertainment',
                'pool': 'Recreation',
                'sports': 'Recreation',
            };
            
            trackActivityModalOpen(
                id,
                activityName,
                categoryMap[id] || 'Other',
                'bento_grid',
                activity.dayPassBadge || false,
                !!activity.badge
            );
        }
        setSelectedActivity(id);
        setIsModalOpen(true);
    };

    // Mobile detection
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768); // md breakpoint
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Reduced motion detection
    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        setPrefersReducedMotion(mediaQuery.matches);
        
        const handleChange = (e: MediaQueryListEvent) => {
            setPrefersReducedMotion(e.matches);
        };
        
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);

    // Scroll depth tracking (optional - only if not using GTM's built-in trigger)
    useEffect(() => {
        // Only add manual scroll tracking if GTM's built-in trigger is not configured
        // GTM's "Scroll Depth" trigger automatically tracks scroll milestones on all pages
        // Uncomment the code below if you need custom scroll tracking behavior
        
        /*
        let scrollMilestones: number[] = [];
        const handleScroll = () => {
            const windowHeight = window.innerHeight;
            const documentHeight = document.documentElement.scrollHeight;
            const scrollTop = window.scrollY || document.documentElement.scrollTop;
            const scrollPercent = Math.round((scrollTop / (documentHeight - windowHeight)) * 100);
            
            // Track milestones: 25%, 50%, 75%, 90%
            [25, 50, 75, 90].forEach(milestone => {
                if (scrollPercent >= milestone && !scrollMilestones.includes(milestone)) {
                    scrollMilestones.push(milestone);
                    trackScrollDepth(milestone);
                }
            });
        };
        
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
        */
    }, []);

    // Get activity subtitles for rotation (matching the order in activitiesList)
    const activitySubtitles = useMemo(() => 
        activitiesList.map(activity => 
            t(`activities.items.${activity.id}.subtitle`)
        ), [t]
    );

    // Subtitle rotation animation (Phase 2) - Pauses when expanded (Phase 4)
    useEffect(() => {
        if (!isMobile || prefersReducedMotion || isExpanded) return; // Pause when expanded

        // Clear any existing interval
        if (subtitleIntervalRef.current) {
            clearInterval(subtitleIntervalRef.current);
        }

        // Set up auto-rotation: change subtitle every 3 seconds
        subtitleIntervalRef.current = setInterval(() => {
            setCurrentSubtitleIndex((prev) => (prev + 1) % activitySubtitles.length);
        }, 3000);

        return () => {
            if (subtitleIntervalRef.current) {
                clearInterval(subtitleIntervalRef.current);
            }
        };
    }, [isMobile, activitySubtitles.length, prefersReducedMotion, isExpanded]);

    return (
        <div className="min-h-screen flex flex-col bg-pebble-beige overflow-x-hidden">
            <SEO
                title={seoData.title}
                description={seoData.description}
                keywords={seoData.keywords}
                canonical="/activities"
                language={seoLang}
                ogTitle={seoData.ogTitle}
                ogDescription={seoData.ogDescription}
            />
            <NewHeader lightBackground={true} />

            {/* 1. HERO BENTO SECTION - Tight fit to header and viewport */}
            <section className={`relative w-full ${isMobile ? 'min-h-auto' : 'h-[calc(100vh-100px)] max-h-[calc(100vh-100px)]'} ${isMobile ? 'mt-[60px] py-2' : 'mt-[100px]'} ${!isMobile ? 'py-1' : ''} flex items-center justify-center px-4 md:px-8 lg:px-10 ${isMobile ? 'overflow-hidden' : 'overflow-visible'}`}>
                {/* Background Ornament - Subtle Backdrop */}
                <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-[url('/ornament-gold.svg')] bg-no-repeat bg-right-top opacity-[0.03] pointer-events-none" />

                <div className="w-full h-full max-w-[1850px] mx-auto flex flex-col relative z-10">
                    {/* Mobile: Hero card at top, Desktop: Bento Grid */}
                    {isMobile ? (
                        /* Mobile Layout: Hero Card at Top */
                        <div className="w-full flex flex-col gap-4">
                            {/* Hero Card - Full width, positioned at top */}
                            <motion.div
                                initial={isMounted ? { opacity: 0, scale: 0.99 } : false}
                                animate={{ opacity: 1, scale: 1 }}
                                className="relative group overflow-hidden rounded-[2.5rem] border border-white/20 shadow-2xl h-[350px] min-h-[350px] shadow-black/10"
                            >
                                <OptimizedImage
                                    src="/images/activity arcade/Bento Grid/bento hero mob.webp"
                                    alt="Beatenberg Hero"
                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                                    priority={true}
                                    fetchPriority="high"
                                    blurDataURL={getBlurPlaceholder("/images/activity arcade/Bento Grid/bento hero mob.webp")}
                                />
                                <div className="absolute inset-0 bg-gradient-to-tr from-charcoal-gray/95 via-charcoal-gray/40 to-transparent" />

                                <div className="absolute inset-0 p-5 md:p-6 flex flex-col justify-end items-start font-lato">
                                    <motion.div
                                        initial={isMounted ? { opacity: 0, x: -25 } : false}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.2 }}
                                        className="bg-alpine-gold text-charcoal-gray text-[10px] md:text-xs font-bold px-4 py-1.5 mb-4 tracking-[0.25em] transform -skew-x-12 uppercase"
                                    >
                                        {t("activities.grid.everythingUnderOneRoof")}
                                    </motion.div>
                                    <h1 className="text-2xl md:text-3xl font-playfair text-white mb-3 leading-[1.05]">
                                        {t("activities.hero.title")}
                                    </h1>
                                    
                                    {/* Animated Subtitle Rotator (Phase 2) */}
                                    <div className="relative h-6 mb-3 overflow-hidden">
                                        <AnimatePresence mode="wait">
                                            <motion.p
                                                key={currentSubtitleIndex}
                                                initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
                                                animate={prefersReducedMotion ? false : { opacity: 1, y: 0 }}
                                                exit={prefersReducedMotion ? false : { opacity: 0, y: -20 }}
                                                transition={prefersReducedMotion ? {} : { duration: 0.5, ease: "easeInOut" }}
                                                className="text-white font-lato text-sm md:text-base font-semibold tracking-[0.1em] uppercase"
                                            >
                                                {activitySubtitles[currentSubtitleIndex]}
                                            </motion.p>
                                        </AnimatePresence>
                                    </div>
                                    
                                    {/* Book Day Pass Button */}
                                    <motion.div
                                        initial={isMounted ? { opacity: 0, y: 10 } : false}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.4 }}
                                        className="mb-3"
                                    >
                                        <Button
                                            onClick={() => {
                                                setIsTrayOpen(true, 'hero_section');
                                            }}
                                            variant="hero"
                                            size="lg"
                                            className="bg-alpine-gold hover:bg-alpine-gold/90 text-white px-6 py-3 text-sm font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                                            trackingType="none"
                                        >
                                            <Calendar size={18} className="mr-2" />
                                            {t("activities.grid.bookDayPass")}
                                        </Button>
                                    </motion.div>

                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-[2px] bg-alpine-gold" />
                                        <p className="text-alpine-gold font-bold tracking-[0.3em] text-[10px] uppercase">
                                            {t("activities.hero.locatedAt")}
                                        </p>
                                    </div>
                                </div>
                            </motion.div>

                            {/* Activity Cards Section - Horizontal Slide (Phase 3) with Expand Toggle (Phase 4) */}
                            <div className="relative w-full min-h-[320px]">
                                {/* Cards Container */}
                                <div className={`relative w-full min-h-[320px] ${isExpanded ? 'overflow-visible' : 'overflow-hidden'}`}>
                                    <div 
                                        className={`flex ${isExpanded ? 'flex-col gap-4' : 'flex-row'} ${prefersReducedMotion ? '' : 'transition-all duration-500 ease-in-out'}`}
                                        style={!isExpanded ? {
                                            transform: `translateX(-${currentSubtitleIndex * 100}%)`
                                        } : {}}
                                    >
                                        {activitiesList.map((activity, index) => {
                                            // Use mobile version if available, otherwise fall back to PC version
                                            const imagePath = activity.imageMobile || activity.image;
                                            // All activity cards are above the fold on mobile, all should have priority
                                            
                                            return (
                                                <div
                                                    key={activity.id}
                                                    className={isExpanded ? "w-full min-h-[320px]" : "min-w-full flex-shrink-0 px-2 min-h-[320px]"}
                                                >
                                                    <ActivityCard
                                                        id={activity.id}
                                                        title={t(`activities.items.${activity.id}.title`)}
                                                        subtitle={t(`activities.items.${activity.id}.subtitle`)}
                                                        description={t(`activities.items.${activity.id}.description`)}
                                                        image={imagePath}
                                                        logo={activity.logo}
                                                        badge={
                                                            activity.badge 
                                                                ? activity.badge 
                                                                : (activity.dayPassBadge ? t("activities.grid.includedInDayPass") : undefined)
                                                        }
                                                        actionType={activity.actionType}
                                                        destination={activity.destination}
                                                        onOpenModal={() => handleOpenModal(activity.id)}
                                                        className="h-[320px] min-h-[320px] rounded-[2.5rem] shadow-none"
                                                        dataOid={`activity-card-mobile-${activity.id}`}
                                                        priority={true}
                                                        dayPassBadge={activity.dayPassBadge || false}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Expand Toggle Button - Below Activity Card (modeled after spa page) */}
                                {!isExpanded && (
                                    <div className="flex justify-center mt-6">
                                        <button
                                            onClick={() => setIsExpanded(true)}
                                            className="group flex flex-col items-center gap-3 transition-all duration-300"
                                        >
                                            <div className="w-12 h-12 rounded-full border border-gray-200 flex items-center justify-center group-hover:border-alpine-gold transition-colors duration-300">
                                                <ChevronDown className="w-6 h-6 text-gray-400 group-hover:text-alpine-gold transition-transform duration-500" />
                                            </div>
                                            <span className="text-[11px] font-semibold uppercase tracking-[0.4em] text-gray-500 group-hover:text-alpine-gold transition-colors duration-300">
                                                {t("activities.grid.viewAllActivities")}
                                            </span>
                                        </button>
                                    </div>
                                )}

                                {/* Collapse Button - When Expanded */}
                                {isExpanded && (
                                    <div className="flex justify-center mt-6">
                                        <button
                                            onClick={() => setIsExpanded(false)}
                                            className="group flex flex-col items-center gap-3 transition-all duration-300"
                                        >
                                            <div className="w-12 h-12 rounded-full border border-gray-200 flex items-center justify-center group-hover:border-alpine-gold transition-colors duration-300">
                                                <ChevronDown className="w-6 h-6 text-gray-400 group-hover:text-alpine-gold transition-transform duration-500 rotate-180" />
                                            </div>
                                            <span className="text-[11px] font-semibold uppercase tracking-[0.4em] text-gray-500 group-hover:text-alpine-gold transition-colors duration-300">
                                                {t("activities.grid.showOneAtATime")}
                                            </span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        /* Desktop Layout: Bento Grid (unchanged) */
                    <div className="grid grid-cols-1 md:grid-cols-[repeat(16,minmax(0,1fr))] md:grid-rows-3 gap-2 md:gap-3 h-full" style={{ gridTemplateRows: 'repeat(3, 1fr)', maxHeight: '100%' }}>
                        {/* Large Vertical Hero Card - Expanded horizontally to 5 columns (1.25x), 2 rows tall */}
                        <motion.div
                            initial={isMounted ? { opacity: 0, scale: 0.99 } : false}
                            animate={{ opacity: 1, scale: 1 }}
                            className="md:col-span-5 md:row-span-2 relative group overflow-hidden rounded-[2.5rem] border border-white/20 shadow-2xl h-full shadow-black/10"
                        >
                            <OptimizedImage
                                src="/images/activity arcade/Bento Grid/bento hero pc.webp"
                                alt="Beatenberg Hero"
                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                                priority={true}
                                fetchPriority="high"
                                blurDataURL={getBlurPlaceholder("/images/activity arcade/Bento Grid/bento hero pc.webp")}
                            />
                            <div className="absolute inset-0 bg-gradient-to-tr from-charcoal-gray/95 via-charcoal-gray/40 to-transparent" />

                            <div className="absolute inset-0 p-8 md:p-10 flex flex-col justify-start items-start font-lato">
                                <motion.div
                                    initial={isMounted ? { opacity: 0, x: -25 } : false}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.2 }}
                                    className="bg-alpine-gold text-charcoal-gray text-[10px] md:text-xs font-bold px-4 py-1.5 mb-5 tracking-[0.25em] transform -skew-x-12 uppercase"
                                >
                                    {t("activities.grid.everythingUnderOneRoof")}
                                </motion.div>
                                <h1 className="text-3xl md:text-3xl lg:text-4xl font-playfair text-white mb-4 leading-[1.05]">
                                    {t("activities.hero.title")}
                                </h1>
                                <p className="text-pebble-beige/80 font-lato text-xs md:text-sm max-w-lg mb-4 italic leading-relaxed">
                                    {t("activities.hero.subtitle")}
                                </p>
                                
                                {/* Book Day Pass Button */}
                                <motion.div
                                    initial={isMounted ? { opacity: 0, y: 10 } : false}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.4 }}
                                    className="mb-4"
                                >
                                    <Button
                                        onClick={() => {
                                            setIsTrayOpen(true, 'hero_section');
                                        }}
                                        variant="hero"
                                        size="lg"
                                        className="bg-alpine-gold hover:bg-alpine-gold/90 text-white px-6 py-3 text-base font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                                        trackingType="none"
                                    >
                                        <Calendar size={18} className="mr-2" />
                                        {t("activities.grid.bookDayPass")}
                                    </Button>
                                </motion.div>

                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-[2px] bg-alpine-gold" />
                                    <p className="text-alpine-gold font-bold tracking-[0.3em] text-[10px] uppercase">
                                        {t("activities.hero.locatedAt")}
                                    </p>
                                </div>
                            </div>
                        </motion.div>

                        {/* Activity Cards (Filling the rest of the 12-cell grid) */}
                        {activitiesList.map((activity, index) => {
                            // Use PC version for desktop (imageMobile is optional)
                            const imagePath = activity.image;
                            // All 8 activity cards are above the fold, all should have priority
                            
                            return (
                                <motion.div
                                    key={activity.id}
                                    initial={isMounted ? { opacity: 0, y: 15 } : false}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.05 * (index + 1), duration: 0.5 }}
                                    className={`h-full min-h-[160px] ${
                                        activity.id === 'sauna' || activity.id === 'bistro_dine_in' 
                                            ? 'md:col-span-7' // 1.75x width (4 * 1.75 = 7 columns)
                                            : 'md:col-span-4' // Standard single card width (4 columns)
                                    }`}
                                >
                                    <ActivityCard
                                        id={activity.id}
                                        title={t(`activities.items.${activity.id}.title`)}
                                        subtitle={t(`activities.items.${activity.id}.subtitle`)}
                                        description={t(`activities.items.${activity.id}.description`)}
                                        image={imagePath}
                                        logo={activity.logo}
                                        badge={
                                            activity.badge 
                                                ? activity.badge 
                                                : (activity.dayPassBadge ? t("activities.grid.includedInDayPass") : undefined)
                                        }
                                        actionType={activity.actionType}
                                        destination={activity.destination}
                                        onOpenModal={() => handleOpenModal(activity.id)}
                                        className="h-full rounded-[2.5rem]"
                                        dataOid={`activity-card-desktop-${activity.id}`}
                                        priority={true}
                                        dayPassBadge={activity.dayPassBadge || false}
                                    />
                                </motion.div>
                            );
                        })}
                    </div>
                    )}
                </div>
            </section>

            {/* 2. VALUE PROPOSITION SECTION */}
            <div className="mt-16 md:mt-24">
                <WhyChooseSection />
            </div>

            {/* 3. DAY PASS PRICING SECTION */}
            <DayPassPricingSection />

            {/* 3.5. DAY GUEST EXPERIENCE VIDEO */}
            <DayGuestExperienceVideo />

            {/* 4. LOCATION & ACCESS SECTION */}
            <LocationAccessSection />

            {/* 5. AMENITIES OVERVIEW SECTION */}
            <AmenitiesOverviewSection />

            {/* 6. FAQ SECTION */}
            <DayGuestFAQ />

            {/* 7. CTA & CONTACT SECTION */}
            <DayGuestCTAContact />

            <Footer />

            {/* Activity Modal */}
            <ActivityModal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setSelectedActivity(null);
                }}
                activity={selectedActivity && activitiesList.find(a => a.id === selectedActivity) ? (() => {
                    const activityId = selectedActivity;
                    const activityData = activitiesList.find(a => a.id === activityId);
                    if (!activityData) return null;

                    // Build extended description array
                    const extendedDescription: string[] = [];
                    try {
                        const desc1 = t(`activities.modal.${activityId}.description`);
                        const desc2 = t(`activities.modal.${activityId}.description2`);
                        const desc3 = t(`activities.modal.${activityId}.description3`);
                        if (desc1 && !desc1.includes('activities.modal')) extendedDescription.push(desc1);
                        if (desc2 && !desc2.includes('activities.modal')) extendedDescription.push(desc2);
                        if (desc3 && !desc3.includes('activities.modal')) extendedDescription.push(desc3);
                    } catch (e) {
                        // Fallback if translations don't exist
                    }

                    // Get Day Pass value proposition
                    let dayPassValue: string | undefined;
                    let dayPassValuePrivate: string | undefined;
                    try {
                        dayPassValue = t(`activities.modal.${activityId}.dayPassValue`);
                        if (dayPassValue.includes('activities.modal')) dayPassValue = undefined;
                        
                        // Get private booking note for cinema
                        if (activityId === 'cinema') {
                            try {
                                dayPassValuePrivate = t(`activities.modal.${activityId}.dayPassValuePrivate`);
                                if (dayPassValuePrivate.includes('activities.modal')) dayPassValuePrivate = undefined;
                            } catch (e) {}
                        }
                    } catch (e) {
                        // Fallback if translation doesn't exist
                    }

                    // Build learn more link
                    let learnMoreLink: { href: string; text: string; isExternal?: boolean } | undefined;
                    try {
                        if (activityId === 'dining') {
                            const learnMoreText = t(`activities.modal.${activityId}.learnMore`);
                            if (learnMoreText && !learnMoreText.includes('activities.modal')) {
                                learnMoreLink = { href: '/dining', text: learnMoreText };
                            }
                        } else if (activityId === 'sauna' || activityId === 'spa') {
                            // Link to spa page
                            const learnMoreText = t(`activities.modal.${activityId}.learnMore`);
                            if (learnMoreText && !learnMoreText.includes('activities.modal')) {
                                learnMoreLink = { href: '/spa', text: learnMoreText };
                            }
                        } else if (activityId === 'bistro_dine_in') {
                            // Link to takeaway website - use learnMoreTakeaway translation
                            try {
                                const takeawayText = t(`activities.modal.${activityId}.learnMoreTakeaway`);
                                if (takeawayText && !takeawayText.includes('activities.modal')) {
                                    learnMoreLink = { 
                                        href: 'https://carbon-mountain-cafe.beatenberg-resortspa.ch/produkte', 
                                        text: takeawayText,
                                        isExternal: true 
                                    };
                                } else {
                                    // Fallback text
                                    learnMoreLink = { 
                                        href: 'https://carbon-mountain-cafe.beatenberg-resortspa.ch/produkte', 
                                        text: 'Visit Takeaway Website',
                                        isExternal: true 
                                    };
                                }
                            } catch (e) {
                                // Fallback if translation doesn't exist
                                learnMoreLink = { 
                                    href: 'https://carbon-mountain-cafe.beatenberg-resortspa.ch/produkte', 
                                    text: 'Visit Takeaway Website',
                                    isExternal: true 
                                };
                            }
                        }
                        // Cinema modal - no learn more link (removed per requirements)
                    } catch (e) {
                        // Fallback if translation doesn't exist
                    }

                    // Special handling for bistro - takeaway link is now handled in learnMoreLink above
                    // No separate takeawayLink needed since learnMoreLink points to takeaway website
                    let takeawayLink: { href: string; text: string; isExternal?: boolean } | undefined;

                    // Menu images
                    let menuImage: string | undefined;
                    if (activityId === 'dining') {
                        menuImage = '/images/activity arcade/jungfrau-menu.webp';
                    } else if (activityId === 'bistro_dine_in') {
                        menuImage = '/images/activity arcade/Carbon Mountain Cafe menu.webp';
                    }

                    // Coming soon badges
                    const isComingSoon = activityId === 'pool' || activityId === 'sports';
                    const comingSoonBadge = activityId === 'cinema' ? t('activities.comingSoon') : undefined;

                    return {
                        id: activityId,
                        title: t(`activities.items.${activityId}.title`),
                        subtitle: t(`activities.items.${activityId}.subtitle`),
                        description: t(`activities.items.${activityId}.description`),
                        image: activityData.image || '',
                        extendedDescription: extendedDescription.length > 0 ? extendedDescription : undefined,
                        dayPassValue,
                        dayPassValuePrivate,
                        learnMoreLink,
                        takeawayLink,
                        menuImage,
                        isComingSoon,
                        comingSoonBadge
                    };
                })() : null}
            />

            {/* Day Pass Booking Tray */}
            <DayPassBookingTray />
        </div>
    );
};

const Activities: React.FC = () => {
    return (
        <DayPassProvider>
            <ActivitiesContent />
        </DayPassProvider>
    );
};

export default Activities;
